import assertNever from "assert-never";
import React, { useState } from "react";
import { ActivityStateMixinState } from "../../activities/ActivityStateMixin";
import { API } from "../../API";
import useMixin from "../../hooks/useMixin";
import { isLoading } from "../../Loading";
import { VisitBroadcastId } from "../../messages/ObjectIds";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { Visit } from "../../system/AppState/AppVisitsState";
import { translate } from "../../translate";
import ErrorScaffold from "../ErrorScaffold";
import { isDefined } from "../Generic/IsDefined";
import LoadingScaffold from "../LoadingScaffold";
import { VisitPendingConfirmationScreen } from "./VisitPendingConfirmationScreen";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import useDirections from "../useDirections";
import DirectionsBottomSheet from "../Patient/components/DirectionsBottomSheet";

interface Props {
  appState: AppState;
  api: API;
  visitId: VisitBroadcastId;
  isReviewOnly: boolean;
  onClickGoBack: () => void;
}

const VisitPendingConfirmationActivity = (props: Props) => {
  const mixin = useMixin(props.appState);
  const state = getState(props.visitId, mixin);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [allCaregiversConfirmed, setAllCaregiversConfirmed] = useState(true);
  const { openLocation, closeLocation, directionsState } = useDirections();

  switch (state.type) {
    case "Loading":
      return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
    case "Ready": {
      const visit = state.visit as Visit.Available;
      const isFlexibleVisit = visit.publicVisitBroadcastDetails.type === "FlexiblePattern";

      const handleConfirm = async () => {
        if (isFlexibleVisit) {
          const result = await props.api.confirmFlexibleVisitAssignmentRequest(props.visitId);
          const needToWaitOtherCaregivers = result.pendingCaregiversCount > 0;
          setAllCaregiversConfirmed(!needToWaitOtherCaregivers);
        } else {
          await props.api.confirmVisitAssignmentRequest(props.visitId);
        }

        setIsConfirmed(true);
      };

      const handleComplete = () => {
        props.onClickGoBack();
        props.appState.refreshVisits();
        props.appState.refreshSchedule();
      };

      const handleCancel = async () => {
        isFlexibleVisit
          ? await props.api.cancelFlexibleVisitAssignmentRequest(props.visitId)
          : await props.api.cancelVisitAssignmentRequest(props.visitId);

        props.appState.refreshVisits();
        props.onClickGoBack();
      };

      const handleClickLocation = () => {
        openLocation([
          visit.publicVisitBroadcastDetails.location.location.lat,
          visit.publicVisitBroadcastDetails.location.location.lng,
        ]);
      };

      return (
        <>
          <DirectionsBottomSheet {...directionsState} onRequestClose={closeLocation} />
          <VisitPendingConfirmationScreen
            visit={visit}
            isConfirmed={isConfirmed}
            allCaregiversConfirmed={allCaregiversConfirmed}
            isReviewOnly={props.isReviewOnly}
            onClickGoBack={props.onClickGoBack}
            caregiverGeoAddress={state.caregiver.addressGeoLocation}
            onConfirm={handleConfirm}
            onComplete={handleComplete}
            onCancel={handleCancel}
            onClickLocation={handleClickLocation}
          />
        </>
      );
    }
    case "Error": {
      const handleClickRetry = () => props.appState.refreshVisits();
      return (
        <ErrorScaffold
          title={translate(STRINGS.PENDING_CONFIRMATION)}
          message={translate(STRINGS.SOMETHING_WENT_WRONG)}
          onClickRetry={handleClickRetry}
          onClickGoBack={props.onClickGoBack}
        />
      );
    }
    default:
      assertNever(state);
  }
};

type VisitPendingConfirmationState =
  | { type: "Loading" }
  | { type: "Ready"; visit: Visit; caregiver: AppCaregiver }
  | { type: "Error" };

const getState = (
  visitId: VisitBroadcastId,
  mixin: ActivityStateMixinState
): VisitPendingConfirmationState => {
  if (isLoading(mixin.appVisitsState) || isLoading(mixin.appCaregiverState)) {
    return { type: "Loading" };
  }

  const visit = mixin.appVisitsState.visits.find((x) => x.id === visitId);
  const caregiver = mixin.appCaregiverState.caregiver;

  if (!isDefined(visit)) {
    return { type: "Error" };
  }

  return { type: "Ready", visit, caregiver };
};

export default VisitPendingConfirmationActivity;
